import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Order } from '@cemex-core/cmx-api';
import { getLegalEntity } from '@cemex/cmx-client-layout';
import { select, Store } from '@ngrx/store';
import { CustomCountlyService } from '@ordersnproducts/modules/countly/services/custom-countly.service';
import { RtlDetectorService } from '@ordersnproducts/modules/shared/services';
import { GlobalFiltersService } from 'app/shared/global-filters/services/global-filters.service';
import * as R from 'ramda';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, first, map, switchMap, take, tap } from 'rxjs/operators';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { OrderCreationIcon, OrderStrategyResponse } from '../../../order-builder/models/order-strategy-response';
import {
  OneClickOrderResetAction,
  OneClickOrdersLoadAction,
  OneClickPlaceOrderAction,
  OneClickSelectOrderAction,
} from '../../actions/one-click-orders.actions';
import { OneClickProfileResetBuilderAction } from '../../actions/one-click-profile.actions';
import { OneClickFeatureConfig } from '../../models/one-click-feature-config';
import { getOneClickState, State } from '../../reducers';
import {
  getOneClickLoading,
  getOneClickOrderValidity,
  getOneClickResult,
  getOneClickSelectedOrder,
  ordersAndTable,
} from '../../selectors/one-click-orders.selector';
import { OneClickConfigLoaderService } from '../../services/one-click-config-loader.service';
import { OneClickElement } from '../../util/enums/one-click-element';
import { OneClickMultipleSelectionComponent } from '../one-click-multiple-selection/one-click-multiple-selection.component';
import { OneClickOrderPreviewComponent } from '../one-click-order-preview/one-click-order-preview.component';
import { OrdersLoad } from '@ordersnproducts/modules/orders-and-product-catalog/modules/orders/actions/orders.actions';

@Component({
  selector: 'app-one-click-modal',
  templateUrl: './one-click-modal.component.html',
  styleUrls: ['./one-click-modal.component.scss', './order-placed-modal.scss'],
})
export class OneClickModalComponent implements OnInit, OnDestroy {
  OneClickElement = OneClickElement;
  @ViewChild(OneClickMultipleSelectionComponent)
  multipleSelectionComponent: OneClickMultipleSelectionComponent;
  @ViewChild(OneClickOrderPreviewComponent) previewComponent: OneClickOrderPreviewComponent;
  public OrderCreationIcon = OrderCreationIcon;
  public result: OrderStrategyResponse;
  public orderSelected$: Observable<Order>;
  public orders: Order[] = [];
  public isFromTable: boolean;
  public isValidOrder: boolean;
  public isLoading = false;
  public features: OneClickFeatureConfig;
  public isReadyToOrder = false;
  public loadings$ = new BehaviorSubject<{
    loading: boolean;
    placing: boolean;
  }>(null);
  public selectedProductLineProfile: string;
  public shouldDisableSubmitButtonFlag: Observable<boolean>;
  public rtl = false;
  private ngDestroy = new Subject();
  private limit: 30;

  constructor(
    private store: Store<State>,
    private dialogRef: MatDialogRef<MatDialog>,
    private router: Router,
    private configService: OneClickConfigLoaderService,
    private customCountlyService: CustomCountlyService,
    public rtlDetector: RtlDetectorService,
    private filtersService: GlobalFiltersService,
  ) {}

  ngOnInit() {
    this.rtl = this.rtlDetector.activeLangRTL();
    this.store.dispatch(new OneClickOrdersLoadAction());
    this.store.select(getOneClickLoading).subscribe((value) => {
      this.loadings$.next(value);
    });
    this.orderSelected$ = this.store.select(getOneClickSelectedOrder);
    this.store
      .select(ordersAndTable)
      .pipe(
        filter(({ orders }) => !!orders),
        take(1),
      )
      .subscribe(({ isFromTable, orders }) => {
        this.orders = orders;
        this.isFromTable = isFromTable;
        this.decideOneClickJourney(isFromTable, orders);
      });

    this.store
      .select(getOneClickResult)
      .pipe(takeUntil(this.ngDestroy))
      .subscribe((result: any) => {
        this.result = result;
        if (result) {
          this.store.dispatch(new OneClickProfileResetBuilderAction());
        }
      });

    this.store
      .select(getOneClickSelectedOrder)
      .pipe(
        filter((selectedProfile) => !!selectedProfile),
        takeUntil(this.ngDestroy),
      )
      .subscribe(
        (selectedProfile) =>
          (this.selectedProductLineProfile = R.pathOr(
            '',
            ['items', 0, 'product', 'productType', 'productTypeDesc'],
            selectedProfile,
          )),
      );

    this.dialogRef.backdropClick().subscribe(() => {
      if (!this.dialogRef.disableClose) {
        this.store.dispatch(new OneClickOrderResetAction());
        this.store.dispatch(new OneClickProfileResetBuilderAction());
      }
    });

    this.dialogRef.afterClosed().subscribe(() => {
      this.store.dispatch(new OneClickOrderResetAction());
      this.store.dispatch(new OneClickProfileResetBuilderAction());
    });
    this.customCountlyService.getUserInfo().pipe(
      take(1),
      map((userInfo) => (this.customCountlyService.userInfo = userInfo)),
    );
    this.shouldDisableSubmitButtonFlag = combineLatest([this.loadings$, this.orderSelected$]).pipe(
      map((results) => {
        return results[0].placing || !results[1];
      }),
    );

    this.orderSelected$
      .pipe(
        filter((order) => !!order),
        take(1),
        switchMap(() => {
          return this.configService.getOneClickConfig().pipe(
            take(1),
            map((config) => config.features),
            tap((features) => (this.features = features)),
          );
        }),
      )
      .subscribe((features) => {
        // Handle order validity. Automatically check if not defined
        const requiresValidity = !!features && !!features[OneClickElement.ORDER_VALIDITY];
        this.checkOrderValidity(requiresValidity);
      });

    this.store.pipe(select(getOneClickState)).subscribe((state) => {
      const isValid = state.oneClickOrders.valid !== null;
      this.isLoading = isValid;
    });
  }
  /**
   * Go history of orders and closes the modal
   */
  navigateToOrders() {
    combineLatest([this.store.select(getLegalEntity), this.orderSelected$])
      .pipe(take(1))
      .subscribe(([legalEntity, order]) => {
        this.filtersService.selectJobsite(legalEntity, order.jobsite.jobsiteId);
        this.router.navigate(['/app/history-of-orders']);
        this.dialogRef.close();
        this.store
          .pipe(
            select(getLegalEntity),
            first((customer) => !!customer),
          )
          .subscribe((customer) => {
            this.store.dispatch(
              new OrdersLoad({
                customerId: customer.legalEntityId,
                fetch: this.limit,
                page: 1,
              }),
            );
          });
      });
  }

  /**
   * Decides single or multiple order journey from side bar or if the order comes from profile table
   * @param oneClickOrders available one click orders
   */
  private decideOneClickJourney(isFromTable: boolean, oneClickOrders: Order[]) {
    if (oneClickOrders.length === 0) {
      this.dialogRef.close(null);
      this.store.dispatch(new OneClickOrderResetAction());
    } else if (oneClickOrders.length === 1 && !isFromTable) {
      this.store.dispatch(new OneClickSelectOrderAction(oneClickOrders[0]));
    }
  }

  public checkOrderValidity(requiresValidity: boolean) {
    if (requiresValidity) {
      this.store
        .select(getOneClickOrderValidity)
        .pipe(
          filter((valid) => valid !== null),
          take(1),
        )
        .subscribe((valid) => {
          this.isValidOrder = valid;
        });
    } else {
      this.isValidOrder = true;
    }
  }

  /**
   * Closes the modal, no action done
   */
  public cancel() {
    this.router.navigate([], { queryParams: { oneclick: null }, queryParamsHandling: 'merge' });
    this.dialogRef.close(null);
    this.store.dispatch(new OneClickOrderResetAction());
  }

  /**
   * Submits one click order template
   */
  public submit() {
    if (this.previewComponent.isFormDataValid()) {
      this.dialogRef.disableClose = true;
      this.store
        .select(getOneClickSelectedOrder)
        .pipe(take(1))
        .subscribe((order) => {
          this.customCountlyService.addEvent('OneClickToBuy', this.customCountlyService.userInfo);
          this.store.dispatch(new OneClickPlaceOrderAction(order));
        });
    }
  }

  /**
   * Sets component ready to place the order
   * Enables/Disables submit button
   * @param isReady
   */
  public setReadyToOrder(isReady: boolean) {
    this.isReadyToOrder = isReady;
  }

  public ngOnDestroy(): void {
    this.ngDestroy.next();
    this.ngDestroy.complete();
  }

  public reset() {
    this.multipleSelectionComponent.reset();
  }
}
